import type { EnterKeyBehavior, PermissionMode } from '@web/api/types'
import { IdentityImage } from '@web/components/Identity'
import { Sheet } from '@web/components/mobile/Sheet'
import { Icon, type IconName } from '@web/components/ui/icons'
import { PillMenu, type PillOption } from '@web/components/ui/PillMenu'
import { PERMISSIONS } from '@web/components/ui/pillCatalog'
import {
  ENTER_KEY_BEHAVIORS,
  enterKeyDescription,
  macKeyboard,
} from '@web/state/enter-key-behavior'
import { navigateWorkspace, useIdentity } from '@web/state/identity'
import {
  saveLocalEnterKeyBehavior,
  useEnterKeyBehavior,
} from '@web/state/local-enter-key-behavior'
import {
  saveTimelinePreferences,
  THINKING_MODES,
  useTimelinePreferences,
} from '@web/state/timeline-preferences'
import {
  type Appearance,
  readAppearance,
  saveAppearance,
} from '@web/theme/appearance'
import { useState } from 'react'

const APPEARANCES: { id: Appearance; label: string; icon: IconName }[] = [
  { id: 'light', label: 'Light', icon: 'sun' },
  { id: 'dark', label: 'Dark', icon: 'moon' },
  { id: 'system', label: 'System', icon: 'desktop' },
]

/** Account, organization and appearance in a drawer over the home, like the session menus. */
export function SettingsSheet({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const identity = useIdentity()
  const [appearance, setAppearance] = useState<Appearance>(readAppearance)
  const [enterKeyError, setEnterKeyError] = useState('')
  const state = identity.state
  const user = state?.current_user
  const enterKeyBehavior = useEnterKeyBehavior(user?.enter_key_behavior)
  const timeline = useTimelinePreferences()
  const org = state?.current_org
  const organizations = state?.organizations ?? []
  const accounts = state?.authorized_users ?? []
  const enterKeyOptions: PillOption[] = ENTER_KEY_BEHAVIORS.map((option) => ({
    ...option,
    description: enterKeyDescription(option.value, macKeyboard()).join(' · '),
  }))
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Settings"
      className="m-settings"
    >
      <div className="m-settings-body">
        <section className="m-settings-card" aria-label="Account">
          <span className="label">Account</span>
          {user && (
            <div className="m-identity-row">
              <IdentityImage src={user.avatar_url} name={user.name} />
              <span className="m-identity-copy">
                <strong>{user.name}</strong>
                <small>{user.is_superuser ? 'Super user' : user.email}</small>
              </span>
            </div>
          )}
          {org && (
            <div className="m-identity-row">
              <IdentityImage src={org.logo_url} name={org.name} organization />
              <span className="m-identity-copy">
                <span className="label">Organization</span>
                <strong>{org.name}</strong>
              </span>
            </div>
          )}
          {organizations.length > 1 && (
            <label className="m-field">
              Switch organization
              <select
                aria-label="Switch organization"
                value={org?.id ?? ''}
                disabled={identity.busy}
                onChange={(event) =>
                  void identity
                    .switchOrganization(event.target.value)
                    .catch(() => {})
                }
              >
                {organizations.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                    {item.id === user?.default_org_id ? ' · Default' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          {accounts.length > 1 && (
            <label className="m-field">
              Switch account
              <select
                aria-label="Switch account"
                value={user?.id ?? ''}
                disabled={identity.busy}
                onChange={(event) =>
                  void identity.switchUser(event.target.value).catch(() => {})
                }
              >
                {accounts.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} · {item.email}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="m-button-row">
            <button
              type="button"
              className="btn"
              disabled={identity.busy}
              onClick={identity.addAccount}
            >
              <Icon name="plus" size={13} />
              Add another account
            </button>
            <button
              type="button"
              className="btn"
              disabled={identity.busy}
              onClick={() => void identity.logout().catch(() => {})}
            >
              <Icon name="end" size={13} />
              Sign out {user?.name.split(' ')[0]}
            </button>
            {accounts.length > 1 && (
              <button
                type="button"
                className="btn"
                disabled={identity.busy}
                onClick={() => void identity.logout(true).catch(() => {})}
              >
                Sign out all accounts
              </button>
            )}
          </div>
          {identity.error && (
            <p role="alert" className="m-error">
              {identity.error}
            </p>
          )}
        </section>
        <section className="m-settings-card" aria-label="Session defaults">
          <label className="m-field">
            Default permission mode
            <select
              aria-label="Default permission mode"
              value={user?.default_permission_mode ?? 'auto'}
              disabled={identity.busy}
              onChange={(event) =>
                void identity
                  .savePreferences(event.target.value as PermissionMode)
                  .catch(() => {})
              }
            >
              {Object.entries(PERMISSIONS).map(([value, option]) => (
                <option key={value} value={value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <p className="m-settings-note">
            Applies to new sessions in every app. Existing sessions keep their
            own permission mode.
          </p>
        </section>
        <section className="m-settings-card" aria-label="Enter Key Behavior">
          <div className="m-option-row">
            <span className="m-option-label">
              Enter Key Behavior
              <small>
                Choose how Enter handles messages in every composer.
              </small>
            </span>
            <PillMenu
              value={enterKeyBehavior}
              options={enterKeyOptions}
              label="Enter Key Behavior"
              tone="field"
              valueLabel="full"
              disabled={identity.busy}
              pending={identity.busy}
              onChange={(value) => {
                setEnterKeyError('')
                const behavior = value as EnterKeyBehavior
                if (!user) {
                  saveLocalEnterKeyBehavior(behavior)
                  return
                }
                void identity
                  .savePreferences({ enter_key_behavior: behavior })
                  .then(() => saveLocalEnterKeyBehavior(behavior))
                  .catch((failure: Error) => setEnterKeyError(failure.message))
              }}
            />
            {enterKeyError && (
              <p role="alert" className="m-error">
                {enterKeyError}
              </p>
            )}
          </div>
        </section>
        <section className="m-settings-card" aria-label="Timeline">
          <span className="label">Timeline</span>
          <label className="m-field">
            Timestamp gap (minutes)
            <input
              type="number"
              min="1"
              max="1440"
              step="1"
              value={timeline.gapMinutes}
              onChange={(event) =>
                saveTimelinePreferences({
                  gapMinutes: event.target.valueAsNumber,
                })
              }
            />
          </label>
          <p className="m-settings-note">
            Show a separator after this much time between messages.
          </p>
          <label className="m-field">
            Thought traces
            <select
              value={timeline.thinkingMode}
              onChange={(event) =>
                saveTimelinePreferences({
                  thinkingMode: event.target
                    .value as typeof timeline.thinkingMode,
                })
              }
            >
              {THINKING_MODES.map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </select>
          </label>
          <p className="m-settings-note">
            {
              THINKING_MODES.find(
                (mode) => mode.value === timeline.thinkingMode,
              )?.description
            }
          </p>
        </section>
        <section className="m-settings-card" aria-label="Appearance">
          <span className="label">Appearance</span>
          <div className="m-appearance" role="group" aria-label="Appearance">
            {APPEARANCES.map((option) => (
              <button
                type="button"
                key={option.id}
                aria-pressed={appearance === option.id}
                onClick={() => {
                  setAppearance(option.id)
                  saveAppearance(option.id)
                }}
              >
                <Icon name={option.icon} size={18} />
                {option.label}
              </button>
            ))}
          </div>
        </section>
        <section className="m-settings-card" aria-label="Integrations">
          <span className="label">Integrations</span>
          <div className="m-button-row">
            <button
              type="button"
              className="btn"
              onClick={() => navigateWorkspace('/?connections=me')}
            >
              <Icon name="repo" size={13} />
              GitHub connections
            </button>
          </div>
          <p className="m-settings-note">
            Connect GitHub so workspace sessions can clone your repositories.
          </p>
        </section>
        <p className="m-settings-version">
          Canopy web · {window.location.host}
        </p>
      </div>
    </Sheet>
  )
}
